import { createClient } from 'contentful'
import React from 'react'
import { ModuleNames } from '../src/constants/componentNames.constants'
import { LocaleCodes } from '../src/constants/localization.constants'
//component formatter
import formatComponentBenefitsData from './component/benefits'
import formatComponentCollapsibleQuadrantData from './component/collapsibleQuadrant'
import formatComponentColumnsData from './component/columns'
import formatComponentContentBlockData from './component/contentBlock'
import formatComponentContentRowsData from './component/contentRows'
import formatComponentDividerData from './component/divider'
import formatComponentFeaturedResourceData from './component/featuredResource'
import formatComponentFeaturedSlider from './component/featuredSlider'
import formatComponentFeaturedTilesData from './component/featuredTiles'
import formatComponentFlexibleHeroData from './component/flexibleHero'
import formatComponentGridData from './component/grid'
import formatComponentHeroData from './component/hero'
import formatComponentIconsTextData from './component/iconsText'
import formatComponentKeyStatsData from './component/keyStats'
import formatComponentMainCTAData from './component/mainCTA'
import formatComponentMarketoFormData from './component/marketoForm'
import formatComponentMediaAssetData from './component/mediaAsset'
import formatComponentMediaTextData from './component/mediaText'
import formatComponentMicrocopySetData from './component/microcopySet'
import formatComponentParagraphBlockData from './component/paragraphBlock'
import formatComponentPlatformDiagramData from './component/platformDiagram'
import formatComponentProductDownloadData from './component/productDownload'
import formatComponentQuoteData from './component/quote'
import formatComponentRawHTML from './component/rawHTML'
import formatResourceListsData from './component/resourceLists'
import formatResourceRowData from './component/resourceRow'
import formatComponentResourceTabsData from './component/resourceTabs'
import formatComponentSliderData from './component/slider'
import formatComponentSpeakerTextData from './component/speakerText'
import formatComponentSpotlightData from './component/spotlight'
import formatComponentTableData from './component/table'
import formatComponentTilesData from './component/tiles'
import formatComponentAnchorLinkData from './topic/anchorLink'
import formatComponentMediaTiles from './topic/mediaTiles'
import formatWrapperImageData from './wrapper/image'
import formatWrapperVideoData from './wrapper/video'
//page data
import getAboutRPAData from './data/aboutRPA'
import getAnalystReportData from './data/analystReport'
import getAnnouncementData from './data/announcement'
import getBlogPostData from './data/blog'
import getCareerData from './data/career'
import getCaseStudyData from './data/caseStudy'
import getCompanyListingData from './data/companyListing'
import getEventsData from './data/events'
import getFeaturedSliderData from './data/featuredSlider'
import getFlexData from './data/flex'
import getFormFlexData from './data/formFlex'
import getHomePageData from './data/homepage'
import getLegalData from './data/legal'
import getBrowserLanguageMicrocopySet from './data/localizedBanner'
import getNavData from './data/nav'
import getNewsroomArticleData from './data/newsroomArticle'
import getParnerData from './data/partner'
import getPartnerPostData from './data/partnerPost'
import getPDFData from './data/pdf'
import getProductData from './data/product'
import getProductDownloadData from './data/productDownload'
import getRawHTMLData from './data/rawHTML'
import getResourceDemoData from './data/resourceDemo'
import getResourceListingData from './data/resourceListing'
import getSecondaryBlogPostData from './data/secondaryBlogs'
import getSolutionsData from './data/solutions'
import getSpeakerBiosMicrocopyData from './data/speakerBiosMicrocopySet'
import getThankYouData from './data/thankYou'
import getWebinarData from './data/webinar'
import getWebinarRecordingData from './data/webinarRecording'
import getWhitePaperData from './data/whitepaper'
import formatTopicCTAData from './topic/cta'
// page component
import BlogPost from '../src/components/page/blog/blogPost/post'
import AuthorListing from '../src/components/page/blog/listing/authorsListing'
import CategoryListing from '../src/components/page/blog/listing/categoryListing'
import CollectionListing from '../src/components/page/blog/listing/collectionListing'
import MainBlog from '../src/components/page/blog/mainBlogLanding'
import Career from '../src/components/page/careers/about'
import Events from '../src/components/page/events/events'
import FormFlex from '../src/components/page/formFlex/formFlexPage'
import HomePage from '../src/components/page/home/homePage'
import LegalPage from '../src/components/page/legal/legalPage'
import BusinessPartners from '../src/components/page/partners/businessPartners/business-partners'
import PartnerPost from '../src/components/page/partners/partnerPost/partnerPost'
import PublicSectorPartners from '../src/components/page/partners/publicSectorPartners/public-sector-partners'
import TechnologyPartners from '../src/components/page/partners/technologyPartners/technology-partners'
import PDF from '../src/components/page/pdf/pdfPage'
import ProductDownload from '../src/components/page/product/download/productDownload'
import ProductPage from '../src/components/page/product/productPage'
import RawHTML from '../src/components/page/rawHTML/rawHTMLPage'
import ResourceDemo from '../src/components/page/resourceDemo/resourceDemoPage'
import AnalystReport from '../src/components/page/resources/analystReport/analystReport'
import CompanyListing from '../src/components/page/resources/company/companyListing/companyListing'
import NewsroomPost from '../src/components/page/resources/company/newsroomPost/newsroomPost'
import CaseStudy from '../src/components/page/resources/customerStory/customerStory'
import ResourceListing from '../src/components/page/resources/listing/listing'
import Webinar from '../src/components/page/resources/webinar/webinar'
import WebinarRecording from '../src/components/page/resources/webinar/webinarRecording/webinarRecording'
import WhitePaper from '../src/components/page/resources/whitePaper/whitePaper'
import ThankYou from '../src/components/page/thankYou/thankYouPage'
import Page404 from '../src/pages/404'

// constants
import moduleMapping from './constants/modulesMapping'
import MODULE_MAPPING from './constants/newModuleMapping'
// shared cache
import { getCookie } from '../src/utils/functions.utils'
import cache from './cache.utils'

/**
 * Get's all the data needed to form the page with slug
 * @param slug
 * @returns page data based on slug
 */

export const getContentfulData = async (slug: string) => {
  const client = createClient({
    space: process.env.CONTENTFUL_SPACE_ID || '',
    accessToken: process.env.CONTENTFUL_PREVIEW_TOKEN || '',
    host: 'preview.contentful.com',
  })

  const { name, code } = pageLocale(slug)
  const pageType = getPageType(slug)

  // check if nav exist for current locale
  if (!cache.get('nav')[name]) {
    let nav = cache.get('nav')
    nav[name] = await getNavData(slug, client)
  }

  // check if announcement exists for current locale
  if (!cache.get('announcement')[name]) {
    let announcement = cache.get('announcement')
    announcement[name] = await getAnnouncementData(slug, client)
  }

  // used for component-speakerText
  if (Object.keys(cache.get('speakerTextMicrocopy')).length === 0) {
    const speakerBioMicrocopy = await getSpeakerBiosMicrocopyData(client)
    cache.set('speakerTextMicrocopy', speakerBioMicrocopy)
  }

  if (!cache.get('browserLanguage')) {
    let data = await getBrowserLanguageMicrocopySet(client)
    cache.set('browserLanguage', data)
  }

  let pageData: any = null
  if (pageType === MODULE_MAPPING.PAGE_PRODUCT) {
    pageData = await getProductData(slug, client)
  } else if (pageType === MODULE_MAPPING.PAGE_SOLUTIONS) {
    pageData = await getSolutionsData(slug, client)
  } else if (pageType === MODULE_MAPPING.HOMEPAGE) {
    pageData = await getHomePageData(slug, client)
  } else if (pageType === MODULE_MAPPING.PAGE_ABOUT_RPA) {
    pageData = await getAboutRPAData(slug, client)
  } else if (pageType === MODULE_MAPPING.PAGE_ANALYST_REPORT) {
    pageData = await getAnalystReportData(slug, client)
  } else if (pageType === MODULE_MAPPING.PAGE_WHITEPAPER) {
    pageData = await getWhitePaperData(slug, client)
  } else if (pageType === MODULE_MAPPING.PAGE_WEBINAR_RECORDING) {
    pageData = await getWebinarRecordingData(slug, client)
  } else if (pageType === MODULE_MAPPING.PAGE_PLACEHOLDER) {
    pageData = await getWebinarData(slug, client)
  } else if (pageType === MODULE_MAPPING.PAGE_RESOURCE) {
    pageData = await getCaseStudyData(slug, client)
  } else if (pageType === MODULE_MAPPING.PAGE_RESOURCE_DEMO) {
    pageData = await getResourceDemoData(slug, client)
  } else if (pageType === MODULE_MAPPING.PAGE_PARTNER_POST) {
    pageData = await getPartnerPostData(slug, client)
  } else if (
    pageType === MODULE_MAPPING.PAGE_BUSINESS_PARTNERS ||
    pageType === MODULE_MAPPING.PAGE_TECHNOLOGY_ALLIANCES ||
    pageType === MODULE_MAPPING.PAGE_PUBLIC_SECTOR_PARTNERS
  ) {
    pageData = await getParnerData(slug, client, pageType)
  } else if (pageType === MODULE_MAPPING.PAGE_CAREER) {
    pageData = await getCareerData(slug, client)
  } else if (pageType === MODULE_MAPPING.PAGE_LEGAL) {
    pageData = await getLegalData(slug, client)
  } else if (pageType === MODULE_MAPPING.PAGE_NEWSROOM_ARTICLE) {
    pageData = await getNewsroomArticleData(slug, client)
  } else if (pageType === MODULE_MAPPING.PAGE_BLOG_POST) {
    pageData = await getBlogPostData(slug, client)
  } else if (pageType === MODULE_MAPPING.PAGE_SECONDARY_BLOG_POST) {
    pageData = await getSecondaryBlogPostData(slug, client)
  } else if (pageType === MODULE_MAPPING.PAGE_RESOURCE_LISTING) {
    pageData = await getResourceListingData(slug, client)
  }

  // if unidentifiable page type, checked the irregular pages with no fix slug prefix
  if (pageData === null || Object.keys(pageData).length === 0) {
    pageData = await getUnknownPageData(slug, client)
  }
  let featuredSliderData = await getFeaturedSliderData(slug, client)

  return {
    pageData: pageData,
    navData: cache.get('nav')[name],
    announcementData: cache.get('announcement')[name],
    browserLanguageMicrocopySet: cache.get('browserLanguage'),
    locale: code,
    featuredSliderData: featuredSliderData,
  }
}

/*
  Use this for unidentifiable pageTypes. Some page types don't have a fixed slug prefix.
*/
export const getUnknownPageData = async (slug: string, client: any) => {
  // Include all the irregular pageTypes here.
  let pageTypes: any = [
    getRawHTMLData,
    getFlexData,
    getAboutRPAData,
    getCompanyListingData,
    getPDFData,
    getProductDownloadData,
    getThankYouData,
    getFormFlexData,
    getEventsData,
  ]
  let allPageData = pageTypes.map((getPageData: any) => getPageData(slug, client))

  let pageData = await Promise.all(allPageData)
  for (let i = 0; i < pageData.length; i++) {
    // Return first entry that has data
    if (Object.keys(pageData[i]).length !== 0 && !pageData[i].error) {
      return pageData[i]
    }
  }

  return {}
}

export const pageLocale = (slug: string) => {
  if (slug) {
    if (slug[0] === '/') {
      slug = slug.slice(1)
    }
    let urlParts = slug.split('/')
    if (urlParts[0] === 'de') {
      return LocaleCodes['GERMAN']
    } else if (urlParts[0] === 'fr') {
      return LocaleCodes['FRENCH']
    } else if (urlParts[0] === 'pt') {
      return LocaleCodes['PORTUGUESE']
    } else if (urlParts[0] === 'es') {
      return LocaleCodes['SPANISH']
    } else if (urlParts[0] === 'ja') {
      return LocaleCodes['JAPANESE']
    } else if (urlParts[0] === 'ko') {
      return LocaleCodes['KOREAN']
    } else if (urlParts[0] === 'cn') {
      return LocaleCodes['CHINESE']
    } else return LocaleCodes['ENGLISH']
  } else {
    return LocaleCodes['ENGLISH']
  }
}

/**
 * Return the page markup given a data set
 * @param data page data (includes page type)
 * @returns
 */
export const pageTemplate = (data: any) => {
  const {
    pageData,
    navData,
    announcementData,
    browserLanguageMicrocopySet,
    locale,
    status,
    featuredSliderData,
  } = data

  const announcement = announcementData?.announcementData?.length
    ? {
        allContentfulComponentAnnouncement: {
          edges: announcementData.announcementData.map((announcement: any) => {
            return { node: announcement }
          }),
        },
      }
    : null

  const mainNavBanner = navData?.mainNavBanner?.length
    ? {
        allContentfulComponentMainNavBanner: {
          edges: navData.mainNavBanner.map((entry: any) => {
            return { node: entry }
          }),
        },
      }
    : null

  const featuredSlider = featuredSliderData?.featuredSlider?.length
    ? {
        allContentfulComponentFeaturedSlider: {
          edges: featuredSliderData.featuredSlider.map((entry: any) => {
            return { node: entry }
          }),
        },
      }
    : null

  const header = navData?.headerData
    ? {
        allContentfulMicrocopySet: {
          edges:
            navData?.navigationMicrocopy?.map((microcopySet: any) => {
              return { node: microcopySet }
            }) || [],
        },
        allContentfulNavMenu: {
          nodes: [navData.headerData],
        },
      }
    : null

  const minHeader = navData?.headerData
    ? {
        allContentfulNavMenu: {
          nodes: [navData.headerData],
        },
      }
    : null

  const footer = navData?.footerData
    ? {
        allContentfulNavMenu: {
          nodes: [navData.footerData],
        },
      }
    : null

  const browserLanguage =
    Object.keys(browserLanguageMicrocopySet).length > 0
      ? {
          contentfulMicrocopySet: browserLanguageMicrocopySet,
        }
      : null

  if (status === 404)
    return (
      <Page404
        headerData={header}
        footerData={footer}
        announcementData={announcement}
        browserLanguageData={browserLanguage}
        lang={locale}
        featuredSliderData={featuredSlider}
      />
    )

  const contentType = pageData.contentType

  let productTemplate = [
    MODULE_MAPPING.PAGE_SOLUTIONS,
    MODULE_MAPPING.PAGE_FLEX,
    MODULE_MAPPING.PAGE_PRODUCT,
    MODULE_MAPPING.PAGE_ABOUT_RPA,
  ]

  const minHeaderTypes = [
    MODULE_MAPPING.PAGE_ANALYST_REPORT,
    MODULE_MAPPING.PAGE_WHITEPAPER,
    MODULE_MAPPING.PAGE_PLACEHOLDER,
    MODULE_MAPPING.PAGE_DOWNLOAD,
    MODULE_MAPPING.PAGE_EVENTS,
  ]

  let Template
  if (productTemplate.includes(contentType)) Template = ProductPage
  else if (contentType === MODULE_MAPPING.HOMEPAGE) Template = HomePage
  else if (contentType === MODULE_MAPPING.PAGE_ANALYST_REPORT) Template = AnalystReport
  else if (contentType === MODULE_MAPPING.PAGE_WHITEPAPER) Template = WhitePaper
  else if (contentType === MODULE_MAPPING.PAGE_PLACEHOLDER) Template = Webinar
  else if (contentType === MODULE_MAPPING.PAGE_PDF) Template = PDF
  else if (contentType === MODULE_MAPPING.PAGE_PARTNER_POST) Template = PartnerPost
  else if (contentType === MODULE_MAPPING.PAGE_CAREER) Template = Career
  else if (contentType === MODULE_MAPPING.PAGE_DOWNLOAD) Template = ProductDownload
  else if (contentType === MODULE_MAPPING.PAGE_BUSINESS_PARTNERS) Template = BusinessPartners
  else if (contentType === MODULE_MAPPING.PAGE_TECHNOLOGY_ALLIANCES) Template = TechnologyPartners
  else if (contentType === MODULE_MAPPING.PAGE_PUBLIC_SECTOR_PARTNERS)
    Template = PublicSectorPartners
  else if (contentType === MODULE_MAPPING.PAGE_RESOURCE) Template = CaseStudy
  else if (contentType === MODULE_MAPPING.PAGE_RESOURCE_DEMO) Template = ResourceDemo
  else if (contentType === MODULE_MAPPING.PAGE_WEBINAR_RECORDING) Template = WebinarRecording
  else if (contentType === MODULE_MAPPING.PAGE_THANK_YOU) Template = ThankYou
  else if (contentType === MODULE_MAPPING.PAGE_LEGAL) Template = LegalPage
  else if (contentType === MODULE_MAPPING.PAGE_NEWSROOM_ARTICLE) Template = NewsroomPost
  else if (contentType === MODULE_MAPPING.PAGE_BLOG_POST) Template = BlogPost
  else if (contentType === MODULE_MAPPING.PAGE_SECONDARY_BLOG_POST) Template = BlogPost
  else if (contentType === MODULE_MAPPING.PAGE_MAIN_BLOG) Template = MainBlog
  else if (contentType === MODULE_MAPPING.PAGE_BLOG_AUTHOR_LISTING) Template = AuthorListing
  else if (contentType === MODULE_MAPPING.PAGE_BLOG_CATEGORY_LISTING) Template = CategoryListing
  else if (contentType === MODULE_MAPPING.PAGE_BLOG_COLLECTION_LISTING) Template = CollectionListing
  else if (contentType === MODULE_MAPPING.PAGE_COMPANY_LISTING) Template = CompanyListing
  else if (contentType === MODULE_MAPPING.PAGE_RESOURCE_LISTING) Template = ResourceListing
  else if (contentType === MODULE_MAPPING.PAGE_FORM_FLEX) Template = FormFlex
  else if (contentType === MODULE_MAPPING.PAGE_EVENTS) Template = Events
  else if (contentType === MODULE_MAPPING.PAGE_RAW_HTML) Template = RawHTML
  //else if (contentType === MODULE_MAPPING.FEATURED_SLIDER) Template = RawHTML

  let props =
    contentType !== MODULE_MAPPING.PAGE_PDF
      ? {
          footerData: footer,
          headerData: minHeaderTypes.includes(contentType) ? minHeader : header,
          announcementData: announcement,
          mainNavBannerData: mainNavBanner,
          browserLanguageData: browserLanguage,
          featuredSliderData: featuredSliderData,
        }
      : {}

  if (Template) return <Template {...pageData.pageData} {...props} />
  else return <div>Page template not found</div>
}

// Format the json response data to match the gatsby schema
export const mapModules = (module: any) => {
  let type = getComponentType(module)
  switch (type) {
    case ModuleNames.FEATURED_SLIDER:
      return formatComponentFeaturedSlider(module)
    case ModuleNames.HERO:
      return formatComponentHeroData(module)
    case ModuleNames.FLEXIBLE_HERO:
      return formatComponentFlexibleHeroData(module)
    case ModuleNames.TILES:
      return formatComponentTilesData(module)
    case ModuleNames.FEATURED_RESOURCE:
      return formatComponentFeaturedResourceData(module)
    case ModuleNames.MEDIA_TEXT:
      return formatComponentMediaTextData(module)
    case ModuleNames.ICONS:
      return formatComponentIconsTextData(module)
    case ModuleNames.VIDEO_SPOTLIGHT:
      return formatComponentSpotlightData(module)
    case ModuleNames.DIVIDER:
      return formatComponentDividerData(module)
    case ModuleNames.GLOBAL_CONTENT_BLOCK:
      return formatComponentContentBlockData(module)
    case ModuleNames.COLLAPSIBLE_QUADRANT:
      return formatComponentCollapsibleQuadrantData(module)
    case ModuleNames.GRID:
      return formatComponentGridData(module)
    case ModuleNames.GLOBAL_KEY_STATS:
      return formatComponentKeyStatsData(module)
    case ModuleNames.BENEFITS:
      return formatComponentBenefitsData(module)
    case ModuleNames.PLATFORM_DIAGRAM:
      return formatComponentPlatformDiagramData(module)
    case ModuleNames.RESOURCE_ROW:
      return formatResourceRowData(module)
    case ModuleNames.RESOURCE_LISTS:
      return formatResourceListsData(module)
    case ModuleNames.GLOBAL_MAIN_CTA:
      return formatComponentMainCTAData(module)
    case ModuleNames.RESOURCE_TABS:
      return formatComponentResourceTabsData(module)
    case ModuleNames.PARAGRAPH_BLOCK:
      return formatComponentParagraphBlockData(module)
    case ModuleNames.QUOTE:
      return formatComponentQuoteData(module)
    case ModuleNames.TABLE:
      return formatComponentTableData(module)
    case ModuleNames.SLIDER:
      return formatComponentSliderData(module)
    case ModuleNames.ANCHOR_LINK:
      return formatComponentAnchorLinkData(module)
    case ModuleNames.WRAPPER_ASSET:
      return formatWrapperImageData(module)
    case ModuleNames.WRAPPER_VIDEO:
      return formatWrapperVideoData(module)
    case ModuleNames.MEDIA_ASSET:
      return formatComponentMediaAssetData(module)
    case ModuleNames.GLOBAL_CONTENT_COLUMNS:
      return formatComponentColumnsData(module)
    case ModuleNames.CONTENT_ROWS:
      return formatComponentContentRowsData(module)
    case ModuleNames.PRODUCT_DOWNLOAD_COMPONENT:
      return formatComponentProductDownloadData(module)
    case ModuleNames.MICROCOPY_SET:
      return formatComponentMicrocopySetData(module)
    case ModuleNames.TOPIC_CTA:
      return formatTopicCTAData(module)
    case ModuleNames.MARKETO_FORM:
      return formatComponentMarketoFormData(module)
    case ModuleNames.SPEAKER_TEXT:
      return formatComponentSpeakerTextData(module)
    case ModuleNames.RAW_HTML:
      return formatComponentRawHTML(module)
    case ModuleNames.FEATURED_TILES:
      return formatComponentFeaturedTilesData(module)
    case ModuleNames.TOPIC_MEDIA_TILES:
      return formatComponentMediaTiles(module)
    default:
      return null
  }
}

export const getComponentType = (component: any) => {
  return moduleMapping[component?.sys?.contentType?.sys?.id]
}

export const getPageType = (slug: string) => {
  if (slug.match('^/?(de|es|ja|ko|pt|es|fr|cn)?/?$')) return MODULE_MAPPING.HOMEPAGE
  if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?product/?.*')) return MODULE_MAPPING.PAGE_PRODUCT
  else if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?solutions/?.*'))
    return MODULE_MAPPING.PAGE_SOLUTIONS
  else if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?newsroom/[2-9]+$'))
    return MODULE_MAPPING.PAGE_COMPANY_LISTING
  else if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?newsroom/.+'))
    return MODULE_MAPPING.PAGE_NEWSROOM_ARTICLE
  else if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?rpa/?.*'))
    return MODULE_MAPPING.PAGE_ABOUT_RPA
  else if (
    slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?resources/automation-analyst-reports(/[0-9]+)?$') ||
    slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?resources/automation-webinars(/[0-9]+)?$') ||
    slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?resources/automation-whitepapers(/[0-9]+)?$') ||
    slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?resources/automation-case-studies(/[0-9]+)?$') ||
    slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?resources/automation-demo(/[0-9]+)?$') ||
    slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?resources/automation-brochures(/[0-9]+)?$') ||
    slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?events(/[0-9]+)?$') ||
    slug.match('^/?resources/all(/[0-9]+)?$') ||
    slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)resources(/[0-9]+)?$')
  ) {
    return MODULE_MAPPING.PAGE_RESOURCE_LISTING
  } else if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?resources/automation-case-studies/.+'))
    return MODULE_MAPPING.PAGE_RESOURCE
  else if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?resources/automation-demo/.+'))
    return MODULE_MAPPING.PAGE_RESOURCE_DEMO
  else if (
    slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?resources/automation-webinars/.*-webinar-recording')
  )
    return MODULE_MAPPING.PAGE_WEBINAR_RECORDING
  else if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?resources/automation-whitepapers/.+'))
    return MODULE_MAPPING.PAGE_WHITEPAPER
  else if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?resources/automation-analyst-reports/.+'))
    return MODULE_MAPPING.PAGE_ANALYST_REPORT
  else if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?resources/automation-webinars/.+'))
    return MODULE_MAPPING.PAGE_PLACEHOLDER
  else if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?partners/technology-alliances(/[0-9]+)?$')) {
    return MODULE_MAPPING.PAGE_TECHNOLOGY_ALLIANCES
  } else if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?partners/business-partners(/[0-9]+)?$')) {
    return MODULE_MAPPING.PAGE_BUSINESS_PARTNERS
  } else if (
    slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?partners/public-sector-partners(/[0-9]+)?$')
  ) {
    return MODULE_MAPPING.PAGE_PUBLIC_SECTOR_PARTNERS
  } else if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?partners/?.*')) {
    return MODULE_MAPPING.PAGE_PARTNER_POST
  } else if (slug.match('^/?company/careers/?$')) {
    return MODULE_MAPPING.PAGE_CAREER
  } else if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?g/thank-you.*')) {
    return MODULE_MAPPING.PAGE_THANK_YOU
  } else if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?legal*')) {
    return MODULE_MAPPING.PAGE_LEGAL
  } else if (slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?blog*')) {
    return MODULE_MAPPING.PAGE_BLOG_POST
  } else if (
    slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)?(inside-the-rocketship|community-blog)/?.*')
  ) {
    return MODULE_MAPPING.PAGE_SECONDARY_BLOG_POST
  } else return ''
}

// Get all entries of a certain content type in a certain locale
export const getAll = async (client: any, contentType: string, query?: any) => {
  let localeCode = query['locale']

  //store by locales
  // only search data with no specific filters, only locales
  if (
    localeCode &&
    cache.get(contentType)?.[localeCode] &&
    Object.keys(query).filter(
      key => key !== 'locale' && key !== 'fields.entryLocale' && key !== 'order'
    ).length === 0
  ) {
    return cache.get(contentType)[localeCode]
  }

  let allData: any = []
  let limit = 100
  let skip = 0

  while (true) {
    try {
      let entries = await client.getEntries({
        content_type: contentType,
        include: 10,
        limit: limit,
        skip: skip,
        ...query,
      })

      // remove circular references
      entries = JSON.parse(entries.stringifySafe())

      allData = allData.concat(entries.items)
      if (skip + limit >= entries.total) break
      skip += limit
    } catch (err) {
      let errMessage = JSON.parse(err?.message || null)
      if (
        errMessage?.message === 'Response size too big. Maximum allowed response size: 7340032B.'
      ) {
        // Reduce limit by 20% if response size is too big
        limit = Math.floor(limit * 0.8)
      } else {
        throw new Error(JSON.stringify(err))
      }
    }
  }

  // only cache data with no specific filters, only locales
  if (
    localeCode &&
    cache.get(contentType) &&
    (Object.keys(query).filter(
      key => key !== 'locale' && key !== 'fields.entryLocale' && key !== 'order'
    ).length === 0 ||
      !query)
  ) {
    cache.get(contentType)[localeCode] = allData
  }

  return allData
}

/*
  If slug is empty, use custom filter; you can't exact match on an empty string, it will yield unexpected results.
 */
export const slugQuery = (slug: string) => {
  return slug ? { 'fields.slug': slug } : { 'fields.slug[exists]': false }
}

// verify if jwt auth token is valid
export const verifyLoginToken = async (cookie: string) => {
  let isValid = false
  let token = getCookie('token', cookie)

  if (!token) return isValid

  try {
    const response = await fetch(`${process.env.GATSBY_PREVIEW_AUTH_API}/verify/www`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify({ token: token }),
    })

    let json = await response.json()

    if (response.status === 200 && json.valid) {
      isValid = true
    }
  } catch (err) {
    console.log('err', err)
    isValid = false
  }

  return isValid
}
